import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EmptyState from '../components/EmptyState';

interface Tier {
  id: string;
  shelf_id: string;
  position: number;
  nickname: string;
  barcode: string;
  shelf_name: string;
  shelf_position: number;
  location_id: string;
  location_name: string;
  items_count: string;
  totes_count: string;
}

interface Tote {
  id: string;
  name: string;
  tier_id: string;
  item_count: string;
  items: Item[];
}

interface Item {
  id: string;
  ic: string;
  sku: string;
  title: string;
  quantity: number;
  condition: string;
  tier_id: string;
  tote_id: string | null;
  tote_name: string | null;
}

export default function TierDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tier, setTier] = useState<Tier | null>(null);
  const [totes, setTotes] = useState<Tote[]>([]);
  const [looseItems, setLooseItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchTierData();
    }
  }, [id]);

  const fetchTierData = async () => {
    try {
      setLoading(true);

      // Fetch tier details
      const tierRes = await fetch(`/api/tiers-pg/${id}`);
      const tierData = await tierRes.json();
      setTier(tierData);

      // Fetch tier inventory
      const invRes = await fetch(`/api/tiers-pg/${id}/inventory`);
      const invData = await invRes.json();

      if (invData.inventory) {
        setTotes(invData.inventory.totes || []);
        setLooseItems(invData.inventory.looseItems || []);
      }
    } catch (error) {
      console.error('Error fetching tier:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        fontFamily: 'Rajdhani, sans-serif',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        minHeight: '100vh',
        padding: '24px',
        color: '#E0E6F0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        Loading tier details...
      </div>
    );
  }

  if (!tier) {
    return (
      <div style={{
        fontFamily: 'Rajdhani, sans-serif',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        minHeight: '100vh',
        padding: '24px',
        color: '#E0E6F0'
      }}>
        <EmptyState
          icon="❌"
          title="Tier Not Found"
          description="The requested tier could not be found."
          actionLabel="← Back to Tiers"
          onAction={() => navigate('/admin/tiers')}
          color="#EF4444"
        />
      </div>
    );
  }

  const totalItems = parseInt(tier.items_count) || 0;
  const totalTotes = parseInt(tier.totes_count) || 0;
  const hasInventory = totalItems > 0 || totalTotes > 0;

  return (
    <div style={{
      fontFamily: 'Rajdhani, sans-serif',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      padding: '24px',
      color: '#E0E6F0'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <h1 style={{
          fontSize: '48px',
          fontWeight: 700,
          color: '#FF6B35',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          margin: 0,
          textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
        }}>
          Tier {tier.position}: {tier.nickname || 'Unnamed'}
        </h1>
        <p style={{
          fontSize: '14px',
          color: '#8A95AA',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginTop: '8px'
        }}>
          {tier.location_name} → {tier.shelf_name} → Tier {tier.position}
        </p>

        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <button
            onClick={() => navigate('/admin/tiers')}
            style={{
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              border: '2px solid rgba(138, 149, 170, 0.4)',
              background: 'rgba(138, 149, 170, 0.1)',
              color: '#8A95AA'
            }}
          >
            ← Back
          </button>
          <button
            onClick={() => window.open(`/api/tiers-pg/${id}/label`, '_blank')}
            style={{
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              border: '2px solid rgba(107, 207, 127, 0.4)',
              background: 'rgba(107, 207, 127, 0.1)',
              color: '#6BCF7F'
            }}
          >
            🖨 Print Label
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          border: '2px solid rgba(0, 240, 255, 0.4)',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '36px', fontWeight: 700, marginBottom: '4px', color: '#00F0FF' }}>
            {tier.position}
          </div>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#8A95AA' }}>
            Position
          </div>
        </div>
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          border: '2px solid rgba(185, 27, 255, 0.4)',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '36px', fontWeight: 700, marginBottom: '4px', color: '#B91BFF' }}>
            {tier.shelf_name}
          </div>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#8A95AA' }}>
            Shelf
          </div>
        </div>
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          border: '2px solid rgba(255, 20, 147, 0.4)',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '36px', fontWeight: 700, marginBottom: '4px', color: '#FF1493' }}>
            {totalTotes}
          </div>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#8A95AA' }}>
            Totes
          </div>
        </div>
        <div style={{
          background: 'rgba(0, 0, 0, 0.4)',
          border: '2px solid rgba(107, 207, 127, 0.4)',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '36px', fontWeight: 700, marginBottom: '4px', color: '#6BCF7F' }}>
            {totalItems}
          </div>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#8A95AA' }}>
            Items
          </div>
        </div>
      </div>

      {/* Empty State */}
      {!hasInventory && (
        <EmptyState
          icon="📦"
          title="No Inventory on This Tier"
          description="This tier is currently empty. Items and totes can be moved to this tier to organize your inventory by height."
          color="#00F0FF"
        />
      )}

      {/* Inventory List */}
      {hasInventory && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.8), rgba(36, 41, 56, 0.8))',
          border: '2px solid rgba(255, 20, 147, 0.3)',
          borderRadius: '12px',
          padding: '20px'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#00F0FF',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '1px solid rgba(255, 20, 147, 0.2)'
          }}>
            Tier Inventory
          </h2>

          {/* Totes */}
          {totes.map(tote => (
            <div key={tote.id} style={{ marginBottom: '16px' }}>
              <div style={{
                color: '#FF1493',
                fontSize: '16px',
                fontWeight: 600,
                marginBottom: '8px'
              }}>
                📦 {tote.name} ({tote.item_count} items)
              </div>
              {tote.items.map(item => (
                <div
                  key={item.id}
                  style={{
                    marginLeft: '20px',
                    padding: '8px',
                    borderLeft: '2px solid rgba(255, 20, 147, 0.3)',
                    marginBottom: '4px',
                    color: '#E0E6F0'
                  }}
                >
                  {item.ic} - {item.title} (Qty: {item.quantity})
                </div>
              ))}
            </div>
          ))}

          {/* Loose Items */}
          {looseItems.length > 0 && (
            <div>
              <div style={{
                color: '#6BCF7F',
                fontSize: '16px',
                fontWeight: 600,
                marginBottom: '8px',
                marginTop: '16px'
              }}>
                Loose Items (No Tote)
              </div>
              {looseItems.map(item => (
                <div
                  key={item.id}
                  style={{
                    marginLeft: '20px',
                    padding: '8px',
                    borderLeft: '2px solid rgba(107, 207, 127, 0.3)',
                    marginBottom: '4px',
                    color: '#E0E6F0'
                  }}
                >
                  {item.ic} - {item.title} (Qty: {item.quantity})
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
